import React from "react";
import {
  DEPARTMENT_DATA_TABLE_COLUMNS,
  TABLE_COLUMNS,
} from "../../../constants/translates";
import { Column } from "../../common/Table/Interfaces";
import { Declarant } from "../../../models/Declarant";
import TextArea from "../columns/textArea";
import {
  CHANGE_CUSTOM_GROUP_FIELD,
  CHANGE_DEPARTMENTS_DATA_FIELD,
  CHANGE_EXPOSED_FIELD_IN_DEPARTMENTS_DATA,
  CHANGE_WARRANTY_CONVERT_IN_DEPARTMENTS_DATA,
} from "../../../constants/events";
import Select from "../columns/select";
import {
  PLATNOSC_VALUES,
  EXPOSED_VALUES,
  NO_KEY,
  YES_KEY,
} from "../../../constants/values";
import {
  ACCESS_TO_DEPARTMENTS_DATA,
  ALL_DATA_IN_DEPARTMENT_DATA,
  ALL_FIELDS,
} from "../../../constants/authorize";
import Exposed from "../declarantTable/columns/exposed";
import ExposedCustomGroup from "./columns/ExposedCustomGroup";
import ExposedFilter from "../declarantTable/filters/selectColumns/exposed";
import Checkbox from "../columns/checkbox";
import CreatorColumn from "./columns/creatorColumn";
import Decimal from "./columns/decimal";
import DepartmentFilter from "../declarantTable/filters/selectColumns/department";

import { CLIENT_ID } from "../../../constants/table";
import Client from "../columns/client";
import CreatorFilter from "../declarantTable/filters/selectColumns/creator";
import ClientFilter from "../declarantTable/filters/selectColumns/client";
import SearchFilter from "../declarantTable/filters/searchFilter";
import dateTimeFilter from "../declarantTable/filters/dateTimeFilter";
import parseDate from "../../../helpers/date";
import { Client as IClient } from "../../../models/ByClients/Client";
import { User } from "../../../models/Users/User";
import { Item } from "../../../models/DepartmentData/Item";
import {
  getConsignmentItemsSum,
  getCustomGroupTotalCost,
  getEpiCost,
  getTotalCost,
  getWarrantySum,
} from "../../../helpers/departmentData";
import Wrapper from "../../customGroup/Wrapper";
import { IndexColumn } from "../IndexColumns";
import { isCustomGroupKey, isCustomGroup } from "../../../helpers/customGroup";
import DestinationCustoms from "./columns/destinationCustoms";
import { Item as ByClientsItem } from "../../../models/ByClients/Item";
import { CustomGroup } from "../../../models/DepartmentData/CustomGroup";
import { DepartmentDataService } from "../../../models/DepartmentData/DepartmentDataService";
import InvoiceDocumentsList from "./invoice-documents-list/InvoiceDocumentsList";
import { getDepartmentDataStore } from "../../../stores/containers/DepartmentDataStore.container";
import EditServicesButton from "./columns/EditServicesButton";
import ViewServicesButton from "./columns/ViewServicesButton";

const COLUMNS = [
  {
    title: TABLE_COLUMNS.INDEX,
    dataIndex: "row_number",
    key: "row_number",
    sorter: true,
    isActive: true,
    order: 1,
    width: 40,
    render: (text, entity: User, index) => (
      <IndexColumn entity={entity} index={index} />
    ),
  },
  {
    title: TABLE_COLUMNS.client,
    dataIndex: "client_id",
    key: "client_id",
    sorter: true,
    isActive: true,
    order: 1,
    width: 150,
    ellipsis: true,
    render: (text, entity: Declarant & IClient) => {
      if (entity.key) {
        return entity.nickname;
      }

      // hide functionality for change client in custom group
      if (entity?.group_id) {
        return entity.client_nickname;
      }

      return (
        <Client
          option={entity.client?.nickname || ""}
          entityKey={CLIENT_ID}
          entityId={(entity as Declarant).id}
          eventKey={CHANGE_DEPARTMENTS_DATA_FIELD}
          permission={ACCESS_TO_DEPARTMENTS_DATA}
        />
      );
    },
    filterDropdown: ClientFilter,
  },
  {
    title: TABLE_COLUMNS.department,
    dataIndex: "department_id",
    key: "department_id",
    sorter: true,
    order: 2,
    width: 150,
    ellipsis: true,
    render: (text, entity: Declarant) => (entity as Declarant).department?.name,
    onlyOnPermission: [ALL_DATA_IN_DEPARTMENT_DATA],
    filterDropdown: DepartmentFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.creator,
    dataIndex: "creator",
    key: "creator",
    sorter: true,
    isActive: true,
    order: 1,
    width: 140,
    ellipsis: true,
    render: (text, entity: Declarant) => <CreatorColumn entity={entity} />,
    filterDropdown: CreatorFilter,
    onlyOnPermission: [ALL_FIELDS],
  },
  {
    title: TABLE_COLUMNS.created_at,
    dataIndex: "creation_date",
    key: "creation_date",
    sorter: true,
    isActive: true,
    order: 1,
    width: 80,
    ellipsis: true,
    render: (text, entity: Declarant) => parseDate(entity.creation_date),
    filterDropdown: dateTimeFilter,
  },
  {
    title: TABLE_COLUMNS.means_of_transportation,
    dataIndex: "means_of_transportation",
    key: "means_of_transportation",
    sorter: true,
    isActive: true,
    order: 1,
    width: 135,
    ellipsis: true,
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.carrier,
    dataIndex: "carrier",
    key: "carrier",
    sorter: true,
    isActive: true,
    order: 1,
    width: 150,
    ellipsis: true,
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.driver,
    dataIndex: "driver",
    key: "driver",
    sorter: true,
    isActive: true,
    order: 1,
    width: 130,
    ellipsis: true,
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.consignment_items,
    dataIndex: "consignment_items",
    key: "consignment_items",
    sorter: true,
    isActive: true,
    order: 1,
    width: 60,
    ellipsis: true,
    filterDropdown: SearchFilter,
    render(text, entity: Declarant & Item) {
      const { children, department_data: departmentData } = entity;

      if (Array.isArray(children) && isCustomGroupKey(entity.id.toString())) {
        if (
          Number(departmentData?.consignment_items_length) &&
          Number(departmentData?.consignment_items)
        ) {
          return `${departmentData?.consignment_items_length}/${departmentData?.consignment_items}`;
        }

        const { length } = children;
        const result = children.reduce(getConsignmentItemsSum, 0) - length;

        return `${length}/${result}`;
      }

      return entity.consignment_items;
    },
  },
  {
    title: TABLE_COLUMNS.cmr,
    dataIndex: "cmr",
    key: "cmr",
    sorter: true,
    isActive: true,
    order: 1,
    width: 90,
    ellipsis: true,
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.date_cmr,
    dataIndex: "date_cmr",
    key: "date_cmr",
    sorter: true,
    isActive: true,
    order: 1,
    width: 80,
    ellipsis: true,
    render: (text, entity: Declarant) => parseDate(entity.date_cmr),
    filterDropdown: dateTimeFilter,
  },
  {
    title: TABLE_COLUMNS.border_checkpoint_name,
    dataIndex: "border_checkpoint_name",
    key: "border_checkpoint_name",
    sorter: true,
    isActive: true,
    order: 1,
    width: 110,
    ellipsis: true,
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.internal_note,
    dataIndex: "internal_note",
    key: "internal_note",
    sorter: true,
    isActive: true,
    order: 1,
    width: 80,
    ellipsis: true,
    filterDropdown: SearchFilter,
    onlyOnPermission: [ALL_FIELDS],
  },
  {
    title: TABLE_COLUMNS.note,
    dataIndex: "note",
    key: "note",
    sorter: true,
    isActive: true,
    order: 1,
    width: 80,
    ellipsis: true,
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.measure_code,
    dataIndex: "measure_code",
    key: "measure_code",
    sorter: true,
    isActive: true,
    order: 1,
    width: 70,
    ellipsis: true,
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.measure_code_doc_number,
    dataIndex: "measure_code_doc_number",
    key: "measure_code_doc_number",
    sorter: true,
    isActive: true,
    order: 1,
    width: 160,
    ellipsis: true,
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.warranty,
    dataIndex: "warranty",
    key: "warranty",
    sorter: true,
    isActive: true,
    order: 1,
    width: 50,
    ellipsis: true,
    filterDropdown: SearchFilter,
    render(text, entity: Declarant & Item) {
      if (Array.isArray(entity.children)) {
        if (isCustomGroupKey(entity.id.toString())) {
          if (entity?.department_data?.warranty) {
            return entity.department_data.warranty;
          }

          return entity.children.reduce(getWarrantySum, 0);
        }

        return entity.warranty;
      }

      return (
        <div className="warranty-container">
          {entity.warranty}
          <Checkbox
            checked={(entity as Declarant).is_warranty_convert}
            entityKey="is_warranty_convert"
            entityId={entity.id}
            eventKey={CHANGE_WARRANTY_CONVERT_IN_DEPARTMENTS_DATA}
            tooltip="Convert to EUR."
          />
        </div>
      );
    },
  },
  {
    title: TABLE_COLUMNS.way,
    dataIndex: "way",
    key: "way",
    sorter: true,
    isActive: true,
    order: 1,
    width: 60,
    ellipsis: true,
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.services,
    dataIndex: "services",
    key: "services",
    sorter: false,
    isActive: true,
    order: 0,
    width: 200,
    ellipsis: false,
    render: (text, entity: Declarant | IClient | Item) => {
      if(isCustomGroup(entity)) {
        const services: DepartmentDataService[] = entity.department_data?.services ?? [];

        return (
          <div className="textarea-preview textarea-preview--services">
            { services.length ? `${services.length} service(s) added` : '' }
            <div className="textarea-preview__actions-group">
              <EditServicesButton groupId={entity.id} services={services} />
              {services.length > 0 && <ViewServicesButton services={services} />}
            </div>
          </div>
        );
      }
    },
  },
  {
    title: TABLE_COLUMNS.services_cost,
    dataIndex: "services_cost",
    key: "services_cost",
    sorter: false,
    isActive: true,
    order: 0,
    width: 110,
    ellipsis: true,
    render: (text, entity: Declarant | IClient | Item) => {
      if (isCustomGroup(entity)) {
        return entity.department_data?.services_sum?.EUR || "0.00";
      }

      return null;
    },
  },
  {
    title: TABLE_COLUMNS.customs_code_and_office_name,
    dataIndex: "customs_code_and_office_name",
    key: "customs_code_and_office_name",
    sorter: true,
    isActive: true,
    order: 1,
    width: 180,
    ellipsis: true,
    filterDropdown: SearchFilter,
    render(value, entity: Declarant & IClient) {
      if (isCustomGroupKey(entity.id.toString())) {
        return (
          <DestinationCustoms
            groupId={entity.key as number | string}
            defaultValue={entity?.department_data?.customs_code_and_office_name}
          />
        );
      }

      return value;
    },
  },
  {
    title: DEPARTMENT_DATA_TABLE_COLUMNS.one_code,
    dataIndex: "one_code",
    key: "one_code",
    sorter: true,
    isActive: true,
    order: 1,
    width: 60,
    ellipsis: true,
    render: (text, entity: Declarant & IClient) => {
      if (isCustomGroupKey(entity.id.toString())) {
        return entity?.department_data?.one_code;
      }

      if (entity.key) {
        return null;
      }

      return (
        <Decimal
          option={entity?.department_data?.one_code || null}
          entityKey="one_code"
          entityId={entity.id}
          eventKey={CHANGE_DEPARTMENTS_DATA_FIELD}
        />
      );
    },
    filterDropdown: SearchFilter,
  },
  {
    title: DEPARTMENT_DATA_TABLE_COLUMNS.next_code,
    dataIndex: "next_code",
    key: "next_code",
    sorter: true,
    isActive: true,
    order: 1,
    width: 60,
    ellipsis: true,
    render: (text, entity: Declarant & IClient) => {
      if (isCustomGroupKey(entity.id.toString())) {
        return entity?.department_data?.next_code;
      }

      if (entity.key) {
        return null;
      }

      return (
        <Decimal
          option={entity?.department_data?.next_code || null}
          entityKey="next_code"
          entityId={entity.id}
          eventKey={CHANGE_DEPARTMENTS_DATA_FIELD}
        />
      );
    },
    filterDropdown: SearchFilter,
  },
  {
    title: DEPARTMENT_DATA_TABLE_COLUMNS.epi_cost,
    dataIndex: "epi_cost",
    key: "epi_cost",
    sorter: true,
    isActive: true,
    order: 1,
    width: 60,
    ellipsis: true,
    render(text, entity: Declarant) {
      return (
        <Wrapper entity={entity} defaultValue={getEpiCost(entity)}>
          <Decimal
            option={entity?.department_data?.epi_cost || null}
            entityKey="epi_cost"
            entityId={entity.id.toString()}
            eventKey={CHANGE_CUSTOM_GROUP_FIELD}
          />
        </Wrapper>
      );
    },
    filterDropdown: SearchFilter,
  },
  {
    title: DEPARTMENT_DATA_TABLE_COLUMNS.t1,
    dataIndex: "t1",
    key: "t1",
    sorter: true,
    isActive: true,
    order: 1,
    width: 60,
    ellipsis: true,
    render(text, entity: Declarant) {
      if (!isCustomGroupKey(entity.id.toString())) {
        return null;
      }

      return (
        <Decimal
          option={entity?.department_data?.t1 ?? null}
          entityKey="t1"
          entityId={entity.id.toString()}
          eventKey={CHANGE_CUSTOM_GROUP_FIELD}
        />
      );
    },
    filterDropdown: SearchFilter,
  },
  {
    title: DEPARTMENT_DATA_TABLE_COLUMNS.guarantee_cost,
    dataIndex: "guarantee_cost",
    key: "guarantee_cost",
    sorter: true,
    isActive: true,
    order: 1,
    width: 60,
    ellipsis: true,
    render(text, entity: Declarant & IClient) {
      // eventKey has been changed if custom group change data
      const eventKey = entity?.children?.length
        ? CHANGE_CUSTOM_GROUP_FIELD
        : CHANGE_DEPARTMENTS_DATA_FIELD;

      return (
        <Wrapper
          entity={entity}
          defaultValue={getEpiCost(entity)}
          forAllRowsExceptRoot
        >
          <Decimal
            option={entity?.department_data?.guarantee_cost || null}
            entityKey="guarantee_cost"
            entityId={entity.id}
            eventKey={eventKey}
          />
        </Wrapper>
      );
    },
    filterDropdown: SearchFilter,
  },
  {
    title: DEPARTMENT_DATA_TABLE_COLUMNS.total_cost,
    dataIndex: "total_cost",
    key: "total_cost",
    sorter: true,
    isActive: true,
    order: 1,
    width: 60,
    ellipsis: true,
    render(text, entity: Declarant) {
      if (entity.id.toString().startsWith("custom_group-")) {
        const total = getCustomGroupTotalCost(entity.department_data);

        if (total !== null) {
          return total;
        }
      }

      return getTotalCost(entity);
    },
    filterDropdown: SearchFilter,
  },
  {
    title: DEPARTMENT_DATA_TABLE_COLUMNS.form_payment,
    dataIndex: "form_payment",
    key: "form_payment",
    sorter: true,
    isActive: true,
    order: 1,
    width: 60,
    ellipsis: true,
    render: (text, entity: Declarant & IClient) => {
      if (entity.key) {
        return null;
      }

      return (
        <Select
          options={PLATNOSC_VALUES}
          entityKey="form_payment"
          entityId={entity.id}
          eventKey={CHANGE_DEPARTMENTS_DATA_FIELD}
          string={entity?.department_data?.form_payment || ""}
        />
      );
    },
    filterDropdown: SearchFilter,
  },
  {
    title: TABLE_COLUMNS.exposed,
    dataIndex: "exposed",
    key: "exposed",
    sorter: true,
    isActive: true,
    order: 1,
    width: 80,
    ellipsis: true,
    filterDropdown: ExposedFilter,
    render: (text, entity: IClient | Declarant | ByClientsItem) => {
      const client = entity as IClient;

      if (
        (client.id as string | number).toString().startsWith("custom_group") &&
        Array.isArray(client.children) &&
        client.children.length > 0
      ) {
        const hasNoDeclarants = client.children.some(
          (item: Declarant | ByClientsItem) => !(item as Declarant).exposed
        );

        return (
          <ExposedCustomGroup
            defaultValue={hasNoDeclarants ? NO_KEY : YES_KEY}
            entity={entity as ByClientsItem}
          />
        );
      }

      return (
        (entity as Declarant).exposed !== undefined && (
          <Exposed
            option={text ? YES_KEY : NO_KEY}
            entityKey="exposed"
            entityId={entity.id}
            options={EXPOSED_VALUES}
            eventKey={CHANGE_EXPOSED_FIELD_IN_DEPARTMENTS_DATA}
            permissions={ACCESS_TO_DEPARTMENTS_DATA}
          />
        )
      );
    },
  },
  {
    title: DEPARTMENT_DATA_TABLE_COLUMNS.invoice_number,
    dataIndex: "invoice_number",
    key: "invoice_number",
    sorter: true,
    isActive: true,
    order: 1,
    width: 180,
    ellipsis: true,
    render: (text, entity: (Declarant & IClient) | CustomGroup) => {
      if ("invoice_documents" in entity) {
        return (
          <InvoiceDocumentsList
            documents={entity.invoice_documents}
            invoiceKey={entity.key}
            store={getDepartmentDataStore()}
          />
        );
      }

      if (entity.key) {
        return null;
      }

      return (
        <TextArea
          string={entity?.department_data?.invoice_number || ""}
          entityKey="invoice_number"
          entityId={entity.id}
          eventKey={CHANGE_DEPARTMENTS_DATA_FIELD}
        />
      );
    },
    filterDropdown: SearchFilter,
  },
] as Column[];

export default COLUMNS;
